import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Alert, Spinner, Badge, Form, Modal, Row, Col, Pagination } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faCheck, faTimes, faTrash, faFilter, faStore, faThumbsUp } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function ValutazioniServizioManagement() {
    const [valutazioni, setValutazioni] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    // Filtri
    const [filtroStato, setFiltroStato] = useState('da_moderare'); // approvate, rifiutate, da_moderare, tutte
    
    // Paginazione
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);
    const [total, setTotal] = useState(0);
    const [hasNext, setHasNext] = useState(false);
    
    // Modal moderazione
    const [showModal, setShowModal] = useState(false);
    const [valutazioneSelezionata, setValutazioneSelezionata] = useState(null);
    const [motivoRifiuto, setMotivoRifiuto] = useState('');
    const [azioneModal, setAzioneModal] = useState(''); // 'approva' o 'rifiuta'
    
    // Modal eliminazione
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [valutazioneDaEliminare, setValutazioneDaEliminare] = useState(null);
    const [motivoEliminazione, setMotivoEliminazione] = useState('');

    // Statistiche
    const [statistiche, setStatistiche] = useState(null);

    useEffect(() => {
        fetchValutazioni();
        fetchStatistiche();
    }, [page, filtroStato]);

    const fetchValutazioni = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('access_token');
            
            let url;
            if (filtroStato === 'da_moderare') {
                url = `${API_URL}/api/valutazioni-servizio/admin/da-moderare?page=${page}&page_size=${pageSize}`;
            } else {
                url = `${API_URL}/api/valutazioni-servizio/admin/tutte?page=${page}&page_size=${pageSize}`;
            }
            
            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            let items = response.data.items;
            
            // Filtra lato client se necessario
            if (filtroStato === 'approvate') {
                items = items.filter(v => v.approvata && !v.motivo_rifiuto);
            } else if (filtroStato === 'rifiutate') {
                items = items.filter(v => !v.approvata && v.motivo_rifiuto);
            }
            
            setValutazioni(items);
            setTotal(response.data.total);
            setHasNext(response.data.has_next);
        } catch (err) {
            console.error('Error fetching valutazioni:', err);
            setError('Errore nel caricamento delle valutazioni servizio');
        } finally {
            setLoading(false);
        }
    };

    const fetchStatistiche = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/valutazioni-servizio/statistiche`);
            setStatistiche(response.data);
        } catch (err) {
            console.error('Error fetching statistiche:', err);
        }
    };

    const handleModera = async (approvare) => {
        try {
            const token = localStorage.getItem('access_token');
            
            await axios.post(
                `${API_URL}/api/valutazioni-servizio/admin/${valutazioneSelezionata.id}/modera`,
                {
                    approvata: approvare,
                    motivo_rifiuto: approvare ? null : motivoRifiuto
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            setSuccess(`Valutazione ${approvare ? 'approvata' : 'rifiutata'} con successo!`);
            setShowModal(false);
            setValutazioneSelezionata(null);
            setMotivoRifiuto('');
            fetchValutazioni();
            fetchStatistiche(); // Aggiorna statistiche
            
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            console.error('Error moderating valutazione:', err);
            setError(err.response?.data?.detail || 'Errore nella moderazione');
        }
    };

    const aprireModalModerazione = (valutazione, azione) => {
        setValutazioneSelezionata(valutazione);
        setAzioneModal(azione);
        setMotivoRifiuto('');
        setShowModal(true);
    };

    const aprireModalEliminazione = (valutazione) => {
        setValutazioneDaEliminare(valutazione);
        setMotivoEliminazione('');
        setShowDeleteModal(true);
    };

    const handleElimina = async () => {
        if (!motivoEliminazione.trim()) {
            setError('Inserisci un motivo per l\'eliminazione');
            return;
        }

        try {
            const token = localStorage.getItem('access_token');
            
            await axios.delete(
                `${API_URL}/api/valutazioni-servizio/admin/${valutazioneDaEliminare.id}?motivo=${encodeURIComponent(motivoEliminazione)}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            
            setSuccess('Valutazione eliminata con successo!');
            setShowDeleteModal(false);
            setValutazioneDaEliminare(null);
            setMotivoEliminazione('');
            fetchValutazioni();
            fetchStatistiche();
            
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            console.error('Error deleting valutazione:', err);
            setError(err.response?.data?.detail || 'Errore nell\'eliminazione');
            setTimeout(() => setError(''), 5000);
        }
    };

    const renderStelle = (rating) => {
        return (
            <span>
                {[1, 2, 3, 4, 5].map(i => (
                    <FontAwesomeIcon
                        key={i}
                        icon={faStar}
                        className={i <= rating ? 'text-warning' : 'text-muted'}
                        style={{ fontSize: '12px', marginRight: '2px' }}
                    />
                ))}
                <span className="ms-1 text-muted">({rating})</span>
            </span>
        );
    };

    const getStatoBadge = (valutazione) => {
        if (valutazione.approvata) {
            return <Badge bg="success">Approvata</Badge>;
        } else if (valutazione.motivo_rifiuto) {
            return <Badge bg="danger">Rifiutata</Badge>;
        } else {
            return <Badge bg="warning">In attesa</Badge>;
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const renderPaginazione = () => {
        const totalPages = Math.ceil(total / pageSize);
        if (totalPages <= 1) return null;

        let items = [];
        for (let number = 1; number <= Math.min(totalPages, 10); number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === page}
                    onClick={() => setPage(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        return (
            <div className="d-flex justify-content-center">
                <Pagination>{items}</Pagination>
            </div>
        );
    };

    return (
        <div className="container mt-4">
            <Row className="mb-4">
                <Col>
                    <h2>
                        <FontAwesomeIcon icon={faStore} className="me-2" />
                        Gestione Valutazioni Servizio
                    </h2>
                </Col>
            </Row>

            {/* Statistiche */}
            {statistiche && (
                <Row className="mb-4">
                    <Col md={3}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <div className="display-6 text-primary">{statistiche.totale_valutazioni}</div>
                                <small className="text-muted">Valutazioni totali</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <div className="display-6 text-warning">
                                    <FontAwesomeIcon icon={faStar} className="me-1" />
                                    {statistiche.media_generale}
                                </div>
                                <small className="text-muted">Media generale</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <div className="display-6 text-success">
                                    <FontAwesomeIcon icon={faThumbsUp} className="me-1" />
                                    {statistiche.percentuale_soddisfazione}%
                                </div>
                                <small className="text-muted">Soddisfazione</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <div className="text-muted small">
                                    <div>Spedizione: ⭐ {statistiche.media_spedizione}</div>
                                    <div>Conformità: ⭐ {statistiche.media_conformita}</div>
                                    <div>Integrità: ⭐ {statistiche.media_integrita}</div>
                                    <div>Comunicazione: ⭐ {statistiche.media_comunicazione}</div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Filtri */}
            <Card className="mb-3">
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>
                                    <FontAwesomeIcon icon={faFilter} className="me-2" />
                                    Stato
                                </Form.Label>
                                <Form.Select 
                                    value={filtroStato} 
                                    onChange={(e) => {
                                        setFiltroStato(e.target.value);
                                        setPage(1);
                                    }}
                                >
                                    <option value="da_moderare">In attesa di moderazione</option>
                                    <option value="approvate">Approvate</option>
                                    <option value="rifiutate">Rifiutate</option>
                                    <option value="tutte">Tutte</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6} className="d-flex align-items-end">
                            <div className="text-muted">
                                Totale valutazioni: <strong>{total}</strong>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
            {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

            {/* Tabella valutazioni */}
            <Card>
                <Card.Header>
                    <h5 className="mb-0">Valutazioni del Servizio</h5>
                </Card.Header>
                <Card.Body className="p-0">
                    {loading ? (
                        <div className="text-center p-4">
                            <Spinner animation="border" /> Caricamento...
                        </div>
                    ) : valutazioni.length === 0 ? (
                        <div className="text-center p-4 text-muted">
                            Nessuna valutazione trovata
                        </div>
                    ) : (
                        <Table responsive hover>
                            <thead className="table-light">
                                <tr>
                                    <th>Cliente</th>
                                    <th>Ordine</th>
                                    <th>Valutazioni</th>
                                    <th>Commento</th>
                                    <th>Data</th>
                                    <th>Stato</th>
                                    <th>Azioni</th>
                                </tr>
                            </thead>
                            <tbody>
                                {valutazioni.map(valutazione => (
                                    <tr key={valutazione.id}>
                                        <td>
                                            <div>
                                                <strong>{valutazione.cliente_nome} {valutazione.cliente_cognome}</strong>
                                            </div>
                                            <small className="text-muted">ID: {valutazione.cliente_id}</small>
                                        </td>
                                        <td>
                                            <span className="badge bg-secondary">#{valutazione.ordine_id}</span>
                                        </td>
                                        <td>
                                            <div style={{ fontSize: '11px', lineHeight: '1.2' }}>
                                                <div>Gen: {renderStelle(valutazione.valutazione_generale)}</div>
                                                <div>Spe: {renderStelle(valutazione.valutazione_spedizione)}</div>
                                                <div>Con: {renderStelle(valutazione.valutazione_conformita)}</div>
                                                <div>Int: {renderStelle(valutazione.valutazione_integrita)}</div>
                                                <div>Com: {renderStelle(valutazione.valutazione_comunicazione)}</div>
                                            </div>
                                        </td>
                                        <td>
                                            {valutazione.commento ? (
                                                <div style={{ maxWidth: '200px' }}>
                                                    <div className="text-truncate">{valutazione.commento}</div>
                                                    {valutazione.commento.length > 50 && (
                                                        <small 
                                                            className="text-primary" 
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => alert(valutazione.commento)}
                                                        >
                                                            Leggi tutto...
                                                        </small>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-muted">-</span>
                                            )}
                                        </td>
                                        <td>
                                            <small>{formatDate(valutazione.data_creazione)}</small>
                                        </td>
                                        <td>
                                            {getStatoBadge(valutazione)}
                                            {valutazione.motivo_rifiuto && (
                                                <div className="mt-1">
                                                    <small className="text-danger">{valutazione.motivo_rifiuto}</small>
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <div className="d-flex gap-1">
                                                {!valutazione.approvata && !valutazione.motivo_rifiuto && (
                                                    <>
                                                        <Button
                                                            size="sm"
                                                            variant="success"
                                                            onClick={() => aprireModalModerazione(valutazione, 'approva')}
                                                            title="Approva"
                                                        >
                                                            <FontAwesomeIcon icon={faCheck} />
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="warning"
                                                            onClick={() => aprireModalModerazione(valutazione, 'rifiuta')}
                                                            title="Rifiuta"
                                                        >
                                                            <FontAwesomeIcon icon={faTimes} />
                                                        </Button>
                                                    </>
                                                )}
                                                <Button
                                                    size="sm"
                                                    variant="danger"
                                                    onClick={() => aprireModalEliminazione(valutazione)}
                                                    title="Elimina"
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            {renderPaginazione()}

            {/* Modal Eliminazione */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title className="text-danger">
                        <FontAwesomeIcon icon={faTrash} className="me-2" />
                        Elimina Valutazione
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {valutazioneDaEliminare && (
                        <div>
                            <div className="alert alert-danger">
                                <strong>Attenzione!</strong> Stai per eliminare definitivamente questa valutazione.
                                Se il cliente ha ricevuto punti fedeltà per questa valutazione, verranno revocati automaticamente.
                            </div>
                            
                            <p><strong>Cliente:</strong> {valutazioneDaEliminare.cliente_nome} {valutazioneDaEliminare.cliente_cognome}</p>
                            <p><strong>Ordine:</strong> #{valutazioneDaEliminare.ordine_id}</p>
                            <p><strong>Stato:</strong> {getStatoBadge(valutazioneDaEliminare)}</p>
                            
                            {valutazioneDaEliminare.commento && (
                                <div className="mb-3">
                                    <strong>Commento:</strong>
                                    <div className="border p-2 mt-1 rounded bg-light">
                                        {valutazioneDaEliminare.commento}
                                    </div>
                                </div>
                            )}
                            
                            <Form.Group className="mt-3">
                                <Form.Label><strong className="text-danger">Motivo dell'eliminazione: *</strong></Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={motivoEliminazione}
                                    onChange={(e) => setMotivoEliminazione(e.target.value)}
                                    placeholder="Specifica il motivo per cui stai eliminando questa valutazione... (es: contenuto inappropriato, duplicata, fraudolenta)"
                                    required
                                />
                                <Form.Text className="text-muted">
                                    Il motivo verrà registrato nei log di sistema.
                                </Form.Text>
                            </Form.Group>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Annulla
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleElimina}
                        disabled={!motivoEliminazione.trim()}
                    >
                        <FontAwesomeIcon icon={faTrash} className="me-2" />
                        Elimina Definitivamente
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal Moderazione */}
            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {azioneModal === 'approva' ? 'Approva Valutazione' : 'Rifiuta Valutazione'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {valutazioneSelezionata && (
                        <div>
                            <p><strong>Cliente:</strong> {valutazioneSelezionata.cliente_nome} {valutazioneSelezionata.cliente_cognome}</p>
                            <p><strong>Ordine:</strong> #{valutazioneSelezionata.ordine_id}</p>
                            
                            <div className="mb-3">
                                <strong>Valutazioni:</strong>
                                <ul className="list-unstyled mt-2">
                                    <li>Generale: {renderStelle(valutazioneSelezionata.valutazione_generale)}</li>
                                    <li>Spedizione: {renderStelle(valutazioneSelezionata.valutazione_spedizione)}</li>
                                    <li>Conformità: {renderStelle(valutazioneSelezionata.valutazione_conformita)}</li>
                                    <li>Integrità: {renderStelle(valutazioneSelezionata.valutazione_integrita)}</li>
                                    <li>Comunicazione: {renderStelle(valutazioneSelezionata.valutazione_comunicazione)}</li>
                                </ul>
                            </div>
                            
                            {valutazioneSelezionata.commento && (
                                <div className="mb-3">
                                    <strong>Commento:</strong>
                                    <div className="border p-2 mt-1 rounded bg-light">
                                        {valutazioneSelezionata.commento}
                                    </div>
                                </div>
                            )}
                            
                            {azioneModal === 'rifiuta' && (
                                <Form.Group>
                                    <Form.Label><strong>Motivo del rifiuto:</strong></Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={motivoRifiuto}
                                        onChange={(e) => setMotivoRifiuto(e.target.value)}
                                        placeholder="Specifica il motivo del rifiuto..."
                                        required
                                    />
                                </Form.Group>
                            )}
                            
                            {azioneModal === 'approva' && (
                                <div className="alert alert-info">
                                    <strong>Attenzione:</strong> Approvando questa valutazione, il cliente riceverà automaticamente i punti fedeltà.
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Annulla
                    </Button>
                    <Button
                        variant={azioneModal === 'approva' ? 'success' : 'danger'}
                        onClick={() => handleModera(azioneModal === 'approva')}
                        disabled={azioneModal === 'rifiuta' && !motivoRifiuto.trim()}
                    >
                        {azioneModal === 'approva' ? 'Approva' : 'Rifiuta'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}